import React, { Component } from 'react';
import { Group } from 'react-konva';
import _isEqual from 'lodash/isEqual';
import {
  delay,
  getHomeX,
  getNumberOfMaxCardsCanBeDropped,
  getPileX,
  getPileY,
  getSlotY,
  haveIntersection,
  hintMove,
  intersectionXDistance,
  moveTo,
} from '../../../utils';
import {
  CardState,
  DropFromLaidToLaidPayload,
  LaidSlotsDropPayload,
  IPile,
  DropFromLaidToHomePayload,
  SuperMove,
  ICardsRenderInfo,
} from '../../../redux/types';
import CardGroup, { changeStateParams } from '../../CardGroup';
import { DraggableCardNumber, MoveTo, onDragEndProps } from './types';
import { getDropElementPosition, IDropElement } from '../../../functions/hints';
import global from '../../../global';

export interface IPileProps {
  pileData: IPile;
  index: number;
  dropElements: IDropElement[];
  superMove: SuperMove | false;
  cardParameters: ICardsRenderInfo;
  numberOfEmptyPiles: number;
  numberOfEmptySlots: number;
  size: number;
  actions: {
    dropFromLaidToLaid: (param: DropFromLaidToLaidPayload) => void;
    dropFromLaidToSlots: (param: LaidSlotsDropPayload) => void;
    dropFromLaidToHome: (param: DropFromLaidToHomePayload) => void;
    setListening: (listening: boolean) => void;
    showCardsOverflowMessage: (visible: boolean, maxCards: number, needCards: number) => void;
  };
}

class Pile extends Component<IPileProps, {}> {
  pile: any = null;
  cardGroup: any = null;

  state = {
    draggableCardNumber: null,
    isDraggingEnd: false,
    isHintMove: false,
  };

  shouldComponentUpdate(nextProps: IPileProps, nextState: any) {
    if (
      !_isEqual(nextProps.pileData, this.props.pileData) ||
      !_isEqual(nextState, this.state) ||
      !_isEqual(nextProps.size, this.props.size)
    ) {
      return true;
    }

    return (
      nextProps.superMove &&
      nextProps.superMove.state === CardState.laid &&
      nextProps.superMove.index === nextProps.index
    );
  }

  onDragStart = (draggableCardNumber: DraggableCardNumber): void => {
    this.pile.moveToTop();
    this.pile.getLayer().moveToTop();
    this.setState({ draggableCardNumber });
    global.sounds.get();
  };

  onDragEnd = (params: onDragEndProps): void => {
    setTimeout(() => {
      const { node, cardIndex, startY, startX } = params;
      const { index, dropElements, actions, pileData, numberOfEmptyPiles, numberOfEmptySlots, size } = this.props;
      const cardName = node.attrs.name;

      this.setState({ isDraggingEnd: true });

      const nodeRect = node.getClientRect({ skipShadow: true, skipStroke: true });

      const matchedDropElementsByName = dropElements.filter(dropElement => {
        return dropElement.dropList.find(item => item.name === cardName);
      });
      const matchDropElements = matchedDropElementsByName.filter(dropElement => {
        const dropElementRect = getDropElementPosition(dropElement, size);
        return haveIntersection(dropElementRect, nodeRect);
      });

      if (!matchDropElements.length) {
        global.sounds.put();
        moveTo(node, startX, startY, 0.1).then(() => {
          this.changePileState({ isDraggingEnd: false, draggableCardNumber: null });
        });
        return;
      }

      let dropElementNumber = 0;

      if (matchDropElements.length > 1) {
        const distances = [
          intersectionXDistance(nodeRect, getDropElementPosition(matchDropElements[0], size)),
          intersectionXDistance(nodeRect, getDropElementPosition(matchDropElements[1], size)),
        ];
        if (distances[1] < distances[0]) dropElementNumber = 1;
      }

      const dropElement = matchDropElements[dropElementNumber];

      if (dropElement.state === CardState.laid) {
        const maxNumberOfCardsThatCanBeDropped = getNumberOfMaxCardsCanBeDropped(
          numberOfEmptySlots,
          numberOfEmptyPiles,
          !Boolean(dropElement.pileSize)
        );

        const pileLength = pileData.length - cardIndex;

        if (pileLength > maxNumberOfCardsThatCanBeDropped) {
          actions.showCardsOverflowMessage(true, maxNumberOfCardsThatCanBeDropped, pileLength);
          global.sounds.put();
          moveTo(node, startX, startY, 0.1).then(() => {
            this.changePileState({ isDraggingEnd: false, draggableCardNumber: null });
          });
          return;
        }
      }

      const dropElementPosition = getDropElementPosition(dropElement, size);
      const cardRect = node.getClientRect({ skipShadow: true, skipStroke: true });
      const x = dropElementPosition.x - cardRect.x + node.x();
      const y = dropElementPosition.y - cardRect.y + node.y();

      global.sounds.put();

      if (dropElement.state === CardState.home) {
        global.sounds.scored();
      }

      moveTo(node, x, y, 0.1).then(() => {
        this.setState({ isDraggingEnd: false, draggableCardNumber: null });

        switch (dropElement.state) {
          case CardState.laid: {
            actions.dropFromLaidToLaid({
              splitNumber: cardIndex,
              pileOneNumber: index,
              pileTwoNumber: dropElement.index,
              undoPosition: { name: node.attrs.name, x: cardRect.x, y: cardRect.y },
            });
            break;
          }

          case CardState.slots: {
            actions.dropFromLaidToSlots({
              pileNumber: index,
              slotNumber: dropElement.index,
            });
            break;
          }

          case CardState.home: {
            actions.dropFromLaidToHome({
              pileNumber: index,
              homeSlotNumber: dropElement.index,
            });
            break;
          }
        }
      });
      //@ts-ignore
    }, 0 || window.dropTimeout);
  };

  onRef = (pile: any): void => {
    this.pile = pile;
  };

  async onDblClick(node: any, draggableCardNumber: DraggableCardNumber) {
    const { dropElements, actions, index, size } = this.props;

    let matchedDropElements = dropElements.filter(dropElement => {
      return dropElement.state === CardState.home && dropElement.dropList.find(item => item.name === node.attrs.name);
    });

    if (!matchedDropElements.length) {
      matchedDropElements = dropElements.filter(dropElement => {
        return (
          dropElement.state === CardState.slots && dropElement.dropList.find(item => item.name === node.attrs.name)
        );
      });
    }

    if (!matchedDropElements.length) return;

    const dropElement = matchedDropElements[0];

    const dropElementPosition = getDropElementPosition(dropElement, size);
    const cardRect = node.getClientRect({ skipShadow: true, skipStroke: true });
    const x = dropElementPosition.x - cardRect.x + node.x();
    const y = dropElementPosition.y - cardRect.y + node.y();

    actions.setListening(false);

    global.sounds.get();

    node.moveToTop();
    this.pile.moveToTop();
    node.getLayer().moveToTop();
    this.setState({ draggableCardNumber, isHintMove: true });

    await delay(50);

    global.sounds.put();

    if (dropElement.state === CardState.home) {
      global.sounds.scored();
    }

    await moveTo(node, x, y);

    switch (dropElement.state) {
      case CardState.slots: {
        actions.dropFromLaidToSlots({
          pileNumber: index,
          slotNumber: dropElement.index,
        });
        break;
      }

      case CardState.home: {
        actions.dropFromLaidToHome({
          pileNumber: index,
          homeSlotNumber: dropElement.index,
        });
        break;
      }
    }
    actions.setListening(true);
  }

  componentDidMount() {
    this.checkSuperMove();
  }

  componentDidUpdate(prevProps: IPileProps) {
    if (!this.props.superMove) return;
    if (!_isEqual(prevProps.superMove, this.props.superMove)) this.checkSuperMove();
  }

  async checkSuperMove() {
    const { superMove, index, actions, size } = this.props;
    if (!superMove) return;
    if (!(superMove.state === CardState.laid && superMove.index === index)) return;

    const stage = this.pile.getStage();
    const superCard = stage.findOne(`.${superMove.name}`);
    const cardRect = superCard.getClientRect({ skipShadow: true, skipStroke: true });
    const homeY = getSlotY(size);
    const homeX = getHomeX(superMove.homeSlot, size);

    const x = homeX - cardRect.x + superCard.x();
    const y = homeY - cardRect.y + superCard.y();

    superCard.moveToTop();
    this.pile.moveToTop();
    superCard.getLayer().moveToTop();

    global.sounds.put();
    global.sounds.scored();

    this.changePileState({ draggableCardNumber: superMove.cardIndex, isHintMove: true });

    await moveTo(superCard, x, y, 0.1).then(() => {
      this.changePileState({});

      actions.dropFromLaidToHome({
        homeSlotNumber: superMove.homeSlot,
        pileNumber: superMove.index || 0,
      });
    });
  }

  changePileState = ({ isDraggingEnd, isHintMove, draggableCardNumber }: changeStateParams) => {
    this.setState({
      isDraggingEnd: isDraggingEnd === undefined ? this.state.isDraggingEnd : isDraggingEnd,
      isHintMove: isHintMove === undefined ? this.state.isHintMove : isHintMove,
      draggableCardNumber: draggableCardNumber === undefined ? this.state.draggableCardNumber : draggableCardNumber,
    });
  };

  moveTo: MoveTo = (card, x, y, cardX, draggableCardNumber) => {
    this.onDragStart(draggableCardNumber);
    this.setState({ draggableCardNumber, isHintMove: true });
    return hintMove(card, x - cardX, y).then(() => {
      this.setState({ draggableCardNumber: null, isHintMove: false });
    });
  };

  render() {
    const { draggableCardNumber, isDraggingEnd, isHintMove } = this.state;
    const { pileData, index, cardParameters, size } = this.props;
    const x = getPileX(index, size);
    const y = getPileY(size);

    if (!pileData.length) return null;

    return (
      <Group x={x} y={y} ref={this.onRef}>
        <CardGroup
          cardParameters={cardParameters}
          ref={cardGroup => (this.cardGroup = cardGroup)}
          cards={pileData}
          onDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
          onDblClick={this.onDblClick.bind(this)}
          draggableCardNumber={draggableCardNumber === undefined ? null : draggableCardNumber}
          isDraggingEnd={isDraggingEnd}
          isHintMove={isHintMove}
          moveTo={this.moveTo}
        />
      </Group>
    );
  }
}

export default Pile;
